import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOperators } from './operators';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useDealLabelsStore } from 'dashboard/stores/dealLabels';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import {
  buildAttributesFilterTypes,
  DEAL_ATTRIBUTES,
} from './helper/filterHelper.js';

/**
 * Composable that provides deal filtering context, used to build the
 * "Negociação" criteria section of an Audience (spec 137). Mirrors
 * useContactFilterContext, matching the fixed fields (and the pipeline_id
 * derived field) Deals::FilterService exposes on the backend.
 * @returns {{ filterTypes: import('vue').ComputedRef<import('./contactProvider.js').FilterType[]> }}
 */
export function useDealFilterContext() {
  const { t } = useI18n();

  const dealAttributes = useMapGetter('attributes/getDealAttributes');
  const dealLabelsStore = useDealLabelsStore();
  const pipelinesStore = usePipelinesStore();

  const {
    operators,
    equalityOperators,
    comparisonOperators,
    dateOperators,
    getOperatorTypes,
  } = useOperators();

  const presenceOnlyOperators = computed(() => [
    operators.value.is_present,
    operators.value.is_not_present,
  ]);

  const pipelineOptions = computed(() =>
    pipelinesStore.getPipelinesList.map(pipeline => ({
      id: pipeline.id,
      name: pipeline.name,
    }))
  );

  const pipelineStageOptions = computed(() =>
    pipelinesStore.getPipelinesList.flatMap(pipeline =>
      (pipeline.stages || []).map(stage => ({
        id: stage.id,
        name: `${stage.name} · ${pipeline.name}`,
      }))
    )
  );

  const customFilterTypes = computed(() =>
    buildAttributesFilterTypes(dealAttributes.value, getOperatorTypes, 'deal')
  );

  const filterTypes = computed(() => [
    {
      attributeKey: DEAL_ATTRIBUTES.NAME,
      value: DEAL_ATTRIBUTES.NAME,
      attributeName: t('AUDIENCES.FILTER.DEAL.NAME'),
      label: t('AUDIENCES.FILTER.DEAL.NAME'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: DEAL_ATTRIBUTES.VALUE,
      value: DEAL_ATTRIBUTES.VALUE,
      attributeName: t('AUDIENCES.FILTER.DEAL.VALUE'),
      label: t('AUDIENCES.FILTER.DEAL.VALUE'),
      inputType: 'plainText',
      dataType: 'number',
      filterOperators: comparisonOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: DEAL_ATTRIBUTES.CURRENCY,
      value: DEAL_ATTRIBUTES.CURRENCY,
      attributeName: t('AUDIENCES.FILTER.DEAL.CURRENCY'),
      label: t('AUDIENCES.FILTER.DEAL.CURRENCY'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: DEAL_ATTRIBUTES.PIPELINE_ID,
      value: DEAL_ATTRIBUTES.PIPELINE_ID,
      attributeName: t('AUDIENCES.FILTER.DEAL.PIPELINE'),
      label: t('AUDIENCES.FILTER.DEAL.PIPELINE'),
      inputType: 'searchSelect',
      options: pipelineOptions.value,
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: DEAL_ATTRIBUTES.PIPELINE_STAGE_ID,
      value: DEAL_ATTRIBUTES.PIPELINE_STAGE_ID,
      attributeName: t('AUDIENCES.FILTER.DEAL.PIPELINE_STAGE'),
      label: t('AUDIENCES.FILTER.DEAL.PIPELINE_STAGE'),
      inputType: 'searchSelect',
      options: pipelineStageOptions.value,
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: DEAL_ATTRIBUTES.CONTACT_ID,
      value: DEAL_ATTRIBUTES.CONTACT_ID,
      attributeName: t('AUDIENCES.FILTER.DEAL.ASSOCIATED_CONTACT'),
      label: t('AUDIENCES.FILTER.DEAL.ASSOCIATED_CONTACT'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: presenceOnlyOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: DEAL_ATTRIBUTES.COMPANY_ID,
      value: DEAL_ATTRIBUTES.COMPANY_ID,
      attributeName: t('AUDIENCES.FILTER.DEAL.ASSOCIATED_COMPANY'),
      label: t('AUDIENCES.FILTER.DEAL.ASSOCIATED_COMPANY'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: presenceOnlyOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: DEAL_ATTRIBUTES.ASSIGNED_TO_ID,
      value: DEAL_ATTRIBUTES.ASSIGNED_TO_ID,
      attributeName: t('AUDIENCES.FILTER.DEAL.ASSIGNED_TO'),
      label: t('AUDIENCES.FILTER.DEAL.ASSIGNED_TO'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: presenceOnlyOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: DEAL_ATTRIBUTES.CREATED_AT,
      value: DEAL_ATTRIBUTES.CREATED_AT,
      attributeName: t('AUDIENCES.FILTER.DEAL.CREATED_AT'),
      label: t('AUDIENCES.FILTER.DEAL.CREATED_AT'),
      inputType: 'date',
      dataType: 'text',
      filterOperators: dateOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: DEAL_ATTRIBUTES.LABELS,
      value: DEAL_ATTRIBUTES.LABELS,
      attributeName: t('AUDIENCES.FILTER.DEAL.LABELS'),
      label: t('AUDIENCES.FILTER.DEAL.LABELS'),
      inputType: 'multiSelect',
      options: dealLabelsStore.getLabels.map(label => ({
        id: label.title,
        name: label.title,
      })),
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    ...customFilterTypes.value,
  ]);

  return { filterTypes };
}
